import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard, Search, Globe } from 'lucide-react';

const Header = () => {
  const { user, isAuthenticated, logout, isProfessional } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    return isProfessional ? '/pro/dashboard' : '/dashboard';
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 glass-header border-b border-slate-100" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="text-2xl font-normal text-slate-900" style={{ fontFamily: '"DM Serif Display", serif' }}>
              HandyVerse
            </span>
            <span className="hidden sm:inline text-xs text-orange-600 font-medium px-2 py-0.5 bg-orange-50 rounded-full">
              Outaouais
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link
              to="/search"
              className={`text-sm font-medium transition-colors ${
                isActive('/search') ? 'text-orange-600' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="nav-search"
            >
              {t('findPro')}
            </Link>
            <Link
              to="/categories"
              className={`text-sm font-medium transition-colors ${
                isActive('/categories') ? 'text-orange-600' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="nav-categories"
            >
              {t('categories')}
            </Link>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-slate-600 hover:text-slate-900"
                  data-testid="language-selector"
                >
                  <Globe className="h-4 w-4" />
                  {language === 'fr' ? 'FR' : 'EN'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  onClick={() => setLanguage('fr')}
                  className={language === 'fr' ? 'bg-slate-100' : ''}
                  data-testid="lang-fr"
                >
                  🇫🇷 Français
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setLanguage('en')}
                  className={language === 'en' ? 'bg-slate-100' : ''}
                  data-testid="lang-en"
                >
                  🇬🇧 English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="rounded-full h-10 px-4 border-2 border-slate-200 hover:bg-slate-50"
                    data-testid="user-menu-trigger"
                  >
                    <User className="h-4 w-4 mr-2" />
                    <span className="font-medium">{user?.first_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()} className="flex items-center cursor-pointer" data-testid="menu-dashboard">
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      {t('dashboard')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-600 hover:text-slate-900" data-testid="login-btn">
                    {t('login')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 btn-press" data-testid="register-btn">
                    {t('register')}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-100 mobile-menu-enter">
            <nav className="flex flex-col gap-2">
              {/* Language Toggle Mobile */}
              <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-50 mb-2">
                <span className="text-sm text-slate-600">{t('language')}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setLanguage('fr')}
                    className={`px-3 py-1 rounded-full text-sm ${language === 'fr' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'}`}
                    data-testid="mobile-lang-fr"
                  >
                    FR
                  </button>
                  <button
                    onClick={() => setLanguage('en')}
                    className={`px-3 py-1 rounded-full text-sm ${language === 'en' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'}`}
                    data-testid="mobile-lang-en"
                  >
                    EN
                  </button>
                </div>
              </div>
              
              <Link
                to="/search"
                className="flex items-center gap-2 px-4 py-3 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-search"
              >
                <Search className="h-5 w-5" />
                {t('findPro')}
              </Link>
              <Link
                to="/categories"
                className="px-4 py-3 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-categories"
              >
                {t('categories')}
              </Link>
              
              {isAuthenticated ? (
                <>
                  <Link
                    to={getDashboardLink()}
                    className="px-4 py-3 rounded-lg hover:bg-slate-50 text-slate-700"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-dashboard"
                  >
                    {t('dashboard')}
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="px-4 py-3 rounded-lg hover:bg-red-50 text-red-600 text-left"
                    data-testid="mobile-logout"
                  >
                    {t('logout')}
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2 pt-4 border-t border-slate-100 mt-2">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full" data-testid="mobile-login-btn">
                      {t('login')}
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-slate-900 hover:bg-slate-800" data-testid="mobile-register-btn">
                      {t('register')}
                    </Button>
                  </Link>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
